<template>
  <div class="container p-4 asig-hub">
    <div class="flex flex-wrap align-items-center gap-2 mb-3">
      <ButtonPrime icon="pi pi-arrow-left" class="p-button-text" @click="router.push({ name: 'estudyoo-hub' })" />
      <h3 class="text-xl font-bold m-0">Asignar actividades por curso</h3>
    </div>

    <DjangoAuthGate @authenticated="onDjangoAuth" />

    <template v-if="djangoOk">
      <p class="text-sm text-600 mb-3">
        Cada asignación es <strong>para un curso de un colegio</strong> (no aplica a todos los colegios a la vez).
        En <strong>Asignar por capítulo</strong> basta con colegio + materia para listar actividades; nivel, curso y capítulo son filtros opcionales (el curso sí se pide al pulsar Asignar).
        En <strong>Todas las asignaciones</strong> solo aparecen vínculos ya creados (para buscar/quitar).
      </p>

      <div
        v-if="diagListo && catalogoVacio"
        class="mb-3 p-3 border-round border-1 border-red-200 bg-red-50"
      >
        <p class="text-sm font-semibold text-red-800 m-0 mb-1">Catálogo vacío en Django</p>
        <p class="text-sm text-red-900 m-0 mb-2">
          Materias: {{ diag.materias }} · Cursos: {{ diag.cursos }} · Colegios: {{ diag.colegios }}
          · Actividades: {{ diag.actividades }} · Capítulos: {{ diag.capitulos }}
        </p>
        <p class="text-xs text-red-800 m-0 mb-2">
          Si creaste actividades y aquí sale 0, puede que estén en otro entorno o falte sync de
          colegio/asignaturas/cursos. Ve al mantenedor de actividades o vuelve a sincronizar.
        </p>
        <div class="flex flex-wrap gap-2">
          <ButtonPrime
            label="Recargar catálogo"
            icon="pi pi-refresh"
            class="p-button-sm"
            :loading="loadingCatalogos || loadingColegios"
            @click="bootstrapCatalogo(true)"
          />
          <ButtonPrime
            label="Ir a actividades"
            icon="pi pi-list"
            class="p-button-sm p-button-outlined"
            @click="router.push({ name: 'estudyoo-actividades' })"
          />
          <ButtonPrime
            label="Ir a sync"
            icon="pi pi-sync"
            class="p-button-sm p-button-outlined"
            @click="router.push({ name: 'sync-django' })"
          />
        </div>
      </div>

      <TabView lazy @tab-change="onTabChange">
        <TabPanel header="Todas las asignaciones">
          <div
            class="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3 p-3 border-round surface-border border-1"
            :class="{ 'asig-filters--dim': loadingCatalogos }"
          >
            <div>
              <label class="block text-sm mb-1">Colegio</label>
              <Dropdown
                v-model="filtTodasColegioId"
                :options="colegioOpts"
                option-label="label"
                option-value="id"
                placeholder="Todos"
                class="w-full"
                filter
                show-clear
                :loading="loadingColegios"
                :disabled="loadingTodas"
                @change="onFiltTodasColegioChange"
              />
            </div>
            <div class="md:col-span-2">
              <label class="block text-sm mb-1">Buscar</label>
              <span class="p-input-icon-left w-full">
                <i class="pi pi-search" />
                <InputText
                  v-model="filtTodasQ"
                  class="w-full"
                  placeholder="Actividad, materia, capítulo, curso…"
                  :disabled="loadingTodas"
                  @keyup.enter="buscarTodasAsignaciones"
                />
              </span>
            </div>
            <div>
              <label class="block text-sm mb-1">Materia</label>
              <Dropdown
                v-model="filtTodasMateria"
                :options="materiaOptsTodas"
                option-label="label"
                placeholder="Todas"
                class="w-full"
                filter
                show-clear
                :loading="loadingCatalogos"
                :disabled="loadingCatalogos"
                @show="cargarCatalogosSiFalta"
              />
            </div>
            <div>
              <label class="block text-sm mb-1">Curso</label>
              <Dropdown
                v-model="filtTodasCurso"
                :options="cursoOptsTodas"
                option-label="label"
                placeholder="Todos"
                class="w-full"
                filter
                show-clear
                :loading="loadingCatalogos"
                :disabled="loadingCatalogos"
                @show="cargarCatalogosSiFalta"
              />
            </div>
            <div>
              <label class="block text-sm mb-1">Estado</label>
              <Dropdown
                v-model="filtTodasActiva"
                :options="opcionesEstadoAsig"
                option-label="label"
                option-value="value"
                class="w-full"
              />
            </div>
            <div class="flex flex-wrap align-items-end gap-2 md:col-span-3">
              <ButtonPrime
                label="Buscar"
                icon="pi pi-search"
                :loading="loadingTodas"
                @click="buscarTodasAsignaciones"
              />
              <ButtonPrime
                label="Limpiar filtros"
                icon="pi pi-filter-slash"
                class="p-button-outlined"
                :disabled="loadingTodas"
                @click="limpiarFiltrosTodas"
              />
            </div>
          </div>

          <div class="flex flex-wrap gap-3 mb-3">
            <div class="asig-stat surface-card border-round p-3 border-1 surface-border">
              <span class="text-xs text-500">Total (filtro actual)</span>
              <p class="text-xl font-bold m-0">{{ todasTotal }}</p>
            </div>
          </div>

          <DataTable
            :value="todasFilas"
            :loading="loadingTodas"
            lazy
            paginator
            :rows="todasRows"
            :total-records="todasTotal"
            :rows-per-page-options="[10, 20, 50]"
            paginator-template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
            current-page-report-template="{first}–{last} de {totalRecords}"
            class="p-datatable-sm"
            empty-message="No hay asignaciones creadas aún. Las actividades sin asignar se ven en la pestaña «Asignar por capítulo»."
            @page="onTodasPage"
          >
            <Column header="Actividad">
              <template #body="{ data }">
                <span class="font-medium">{{ data.actividad_titulo || '—' }}</span>
              </template>
            </Column>
            <Column header="Capítulo">
              <template #body="{ data }">
                {{ data.capitulo_nombre || '—' }}
                <span v-if="data.capitulo_codigo" class="text-xs text-500">({{ data.capitulo_codigo }})</span>
              </template>
            </Column>
            <Column header="Materia">
              <template #body="{ data }">{{ data.materia_nombre || '—' }}</template>
            </Column>
            <Column header="Curso">
              <template #body="{ data }">{{ labelCursoRow(data) }}</template>
            </Column>
            <Column header="Colegio">
              <template #body="{ data }">{{ data.colegio_nombre || '—' }}</template>
            </Column>
            <Column header="Estado" style="width: 7rem">
              <template #body="{ data }">
                <TagPrime
                  :severity="data.activa !== false ? 'success' : 'secondary'"
                  :value="data.activa !== false ? 'Activa' : 'Inactiva'"
                />
              </template>
            </Column>
            <Column header="Acción" style="width: 8rem">
              <template #body="{ data }">
                <ButtonPrime
                  label="Quitar"
                  icon="pi pi-times"
                  class="p-button-sm p-button-outlined p-button-danger"
                  :loading="asignandoId === data.id"
                  @click="quitarAsignacionTodas(data)"
                />
              </template>
            </Column>
          </DataTable>
        </TabPanel>

        <TabPanel header="Asignar por capítulo">
      <div
        v-if="loadingCatalogos"
        class="asig-loading-banner mb-3 p-3 border-round border-1 surface-border flex align-items-center gap-2"
        role="status"
        aria-live="polite"
      >
        <i class="pi pi-spin pi-spinner text-primary" />
        <span class="text-sm">Cargando materias y cursos…</span>
      </div>
      <div
        v-else-if="loadingCapitulos"
        class="asig-loading-banner mb-3 p-3 border-round border-1 surface-border flex align-items-center gap-2"
        role="status"
        aria-live="polite"
      >
        <i class="pi pi-spin pi-spinner text-primary" />
        <span class="text-sm">
          Cargando capítulos
          <template v-if="selMateria && selNivel">
            · {{ selMateria.nombre }} · {{ labelNivel(selNivel) }}
          </template>
          …
        </span>
      </div>

      <div
        class="grid grid-cols-1 md:grid-cols-6 gap-3 mb-3 p-3 border-round surface-border border-1"
        :class="{ 'asig-filters--dim': loadingCatalogos || loadingCapitulos }"
      >
        <div>
          <label class="block text-sm mb-1">Colegio <span class="text-red-500">*</span></label>
          <Dropdown
            v-model="selColegioId"
            :options="colegioOpts"
            option-label="label"
            option-value="id"
            placeholder="Elegir colegio"
            class="w-full"
            filter
            show-clear
            :loading="loadingColegios"
            :disabled="loadingCatalogos"
            @change="onColegioChange"
          />
        </div>
        <div>
          <label class="block text-sm mb-1">Materia <span class="text-red-500">*</span></label>
          <Dropdown
            :key="`mat-${materiaOpts.length}-${loadingCatalogos}-${selColegioId}`"
            v-model="selMateria"
            :options="materiaOpts"
            option-label="label"
            placeholder="Elegir materia"
            class="w-full"
            filter
            show-clear
            :loading="loadingCatalogos"
            :disabled="!selColegioId || loadingCatalogos"
            @change="onMateriaChange"
          />
        </div>
        <div>
          <label class="block text-sm mb-1">Nivel <span class="text-xs text-500">(opcional)</span></label>
          <Dropdown
            v-model="selNivel"
            :options="nivelOpts"
            option-label="label"
            option-value="value"
            placeholder="Filtrar por nivel"
            class="w-full"
            filter
            show-clear
            :disabled="!selColegioId"
            @change="onNivelChange"
          />
        </div>
        <div>
          <label class="block text-sm mb-1">
            Curso destino
            <span class="text-red-500">*</span>
            <span class="text-xs text-500"> (obligatorio para asignar)</span>
          </label>
          <Dropdown
            :key="`cur-${selColegioId}`"
            v-model="selCurso"
            :options="cursoOpts"
            option-label="label"
            data-key="id"
            :placeholder="cursoPlaceholder"
            class="w-full"
            :class="{ 'p-invalid': panelListo && !selCurso }"
            filter
            show-clear
            :loading="loadingCatalogos"
            :disabled="!selColegioId || loadingCatalogos"
            @change="onCursoChange"
          />
          <p v-if="selColegioId && !loadingCatalogos && !cursoOpts.length" class="text-xs text-orange-700 mt-1 mb-0">
            No hay cursos para este colegio{{ selNivel ? ` en ${labelNivel(selNivel)}` : '' }}.
            Prueba quitando el filtro de nivel o sincroniza cursos.
          </p>
        </div>
        <div>
          <label class="block text-sm mb-1">Capítulo <span class="text-xs text-500">(opcional)</span></label>
          <Dropdown
            :key="`cap-${capitulos.length}-${loadingCapitulos}`"
            v-model="selCapitulo"
            :options="capituloOpts"
            option-label="label"
            :placeholder="capituloPlaceholder"
            class="w-full"
            filter
            show-clear
            :disabled="!selMateria || loadingCapitulos"
            :loading="loadingCapitulos"
            @change="onCapituloChange"
          />
          <p v-if="selMateria && !loadingCapitulos" class="text-xs text-500 mt-1 mb-0">
            {{ capituloOpts.length }} capítulo(s) disponibles
          </p>
          <p v-else-if="selMateria && loadingCapitulos" class="text-xs text-500 mt-1 mb-0">
            <i class="pi pi-spin pi-spinner text-xs mr-1" />Obteniendo capítulos…
          </p>
        </div>
        <div class="flex flex-wrap align-items-end gap-2">
          <ButtonPrime
            label="Cargar"
            icon="pi pi-search"
            :loading="loading"
            :disabled="loadingCatalogos || loadingCapitulos"
            @click="cargarPanel"
          />
          <ButtonPrime
            label="Limpiar"
            icon="pi pi-filter-slash"
            class="p-button-outlined"
            :disabled="loadingCatalogos"
            @click="limpiar"
          />
        </div>
      </div>

      <div
        v-if="materiasSinAlcanceColegio"
        class="mb-3 p-3 border-round border-1 border-orange-200 bg-orange-50"
      >
        <p class="text-sm text-orange-900 m-0">
          Este colegio no tiene materias propias en Django (el API
          <code>?colegio_id=…</code> devolvió vacío). Se listan todas las materias disponibles.
          Re-sincroniza <strong>asignaturas</strong> para este colegio para asociarlas correctamente.
        </p>
      </div>

      <div
        v-if="capitulosSinNivel.length && !loadingCapitulosMateria"
        class="mb-3 p-3 border-round border-1 border-orange-200 bg-orange-50"
      >
        <p class="text-sm font-semibold text-orange-800 m-0 mb-2">
          {{ capitulosSinNivel.length }} capítulo(s) de esta materia sin nivel curricular
        </p>
        <p class="text-xs text-orange-700 m-0 mb-2">
          Vienen del sync sin <code>IdCurso</code> o sin nivel. Asigna el nivel del curso seleccionado o re-sincroniza.
        </p>
        <ul class="text-xs text-orange-800 m-0 pl-3 mb-2">
          <li v-for="c in capitulosSinNivel.slice(0, 8)" :key="c.id">
            {{ c.nombre }}{{ c.codigo ? ` (${c.codigo})` : '' }}
          </li>
          <li v-if="capitulosSinNivel.length > 8">… y {{ capitulosSinNivel.length - 8 }} más</li>
        </ul>
        <ButtonPrime
          v-if="selNivel && selCapitulo && !selCapitulo.nivel_simce"
          label="Asignar nivel a este capítulo"
          icon="pi pi-wrench"
          class="p-button-sm p-button-outlined"
          :loading="corrigiendoNivel"
          @click="corregirNivelCapitulo"
        />
      </div>

      <div
        v-if="selCapitulo && !selCapitulo.nivel_simce && selNivel"
        class="mb-3 p-3 border-round border-1 border-yellow-200 bg-yellow-50"
      >
        <p class="text-sm text-yellow-900 m-0 mb-2">
          El capítulo <strong>{{ selCapitulo.nombre }}</strong> no tiene nivel. Los alumnos podrían no ver las actividades.
        </p>
        <ButtonPrime
          :label="`Vincular a ${labelNivel(selNivel)}`"
          icon="pi pi-link"
          class="p-button-sm"
          :loading="corrigiendoNivel"
          @click="corregirNivelCapitulo"
        />
      </div>

      <EstudyooLoadingBanner
        :visible="loading && !panelListo"
        message="Cargando actividades y asignaciones…"
      />

      <template v-if="panelListo">
        <div
          v-if="!selCurso"
          class="mb-3 p-3 border-round border-1 border-orange-300 bg-orange-50"
        >
          <p class="text-sm text-orange-900 m-0 font-semibold">
            El botón <strong>Asignar</strong> está bloqueado hasta elegir un curso destino.
          </p>
          <p class="text-sm text-orange-800 m-0 mt-1">
            Arriba, en <strong>Curso destino</strong>, elige la sección (ej. 4° básico A).
            Sin curso no se puede publicar la actividad al catálogo del alumno.
          </p>
        </div>

        <div class="flex flex-wrap gap-3 mb-3">
          <div class="asig-stat surface-card border-round p-3 border-1 surface-border">
            <span class="text-xs text-500">Actividades</span>
            <p class="text-xl font-bold m-0">{{ filas.length }}</p>
          </div>
          <div class="asig-stat surface-card border-round p-3 border-1 surface-border">
            <span class="text-xs text-500">{{ selCurso ? 'Asignadas al curso' : 'Ya asignadas (colegio)' }}</span>
            <p class="text-xl font-bold m-0 text-green-600">{{ asignadasCount }}</p>
          </div>
          <div class="asig-stat surface-card border-round p-3 border-1 surface-border">
            <span class="text-xs text-500">Pendientes</span>
            <p class="text-xl font-bold m-0 text-orange-600">{{ pendientesCount }}</p>
          </div>
        </div>

        <div class="flex flex-wrap gap-2 mb-3">
          <ButtonPrime
            label="Asignar todas las pendientes"
            icon="pi pi-check-circle"
            :loading="asignandoTodas"
            :disabled="!pendientesCount"
            :title="selCurso ? 'Asignar todas al curso' : 'Primero elige un curso destino arriba'"
            @click="asignarTodasPendientes"
          />
          <ButtonPrime
            :label="soloPendientes ? 'Ver todas' : 'Solo pendientes'"
            :icon="soloPendientes ? 'pi pi-list' : 'pi pi-filter'"
            class="p-button-outlined"
            :disabled="!filas.length"
            @click="soloPendientes = !soloPendientes"
          />
          <ButtonPrime
            label="Recargar"
            icon="pi pi-refresh"
            class="p-button-outlined"
            :loading="loading"
            @click="cargarPanel"
          />
        </div>

        <p class="text-xs text-500 mb-2">
          {{ colegioNombrePorId[String(selColegioId)] || '—' }}
          · {{ labelMateria(selMateria) }}
          <template v-if="selNivel"> · {{ labelNivel(selNivel) }}</template>
          <template v-if="selCurso"> · {{ labelCurso(selCurso) }}</template>
          <template v-if="selCapitulo"> · {{ selCapitulo.nombre }}</template>
          <template v-else> · todos los capítulos</template>
        </p>

        <DataTable
          :value="filasVista"
          :loading="loading"
          class="p-datatable-sm"
          :empty-message="soloPendientes ? 'No hay actividades pendientes con estos filtros' : 'Sin actividades con estos filtros'"
        >
          <Column field="titulo" header="Actividad" />
          <Column header="Capítulo">
            <template #body="{ data }">{{ labelCapituloActividad(data) }}</template>
          </Column>
          <Column header="Tipo">
            <template #body="{ data }">{{ labelTipo(data.tipo || data.tipo_id) }}</template>
          </Column>
          <Column header="Estado">
            <template #body="{ data }">
              <TagPrime
                :severity="data.asignada ? 'success' : 'warn'"
                :value="data.asignada ? (selCurso ? 'Asignada' : 'Asignada en colegio') : 'Pendiente'"
              />
            </template>
          </Column>
          <Column header="Acción" style="width: 10rem">
            <template #body="{ data }">
              <ButtonPrime
                v-if="!data.asignada"
                label="Asignar"
                icon="pi pi-plus"
                class="p-button-sm p-button-outlined"
                :loading="asignandoId === data.id"
                :title="selCurso ? 'Asignar al curso seleccionado' : 'Primero elige un curso destino arriba'"
                @click="asignarUna(data)"
              />
              <ButtonPrime
                v-else-if="data.asignacionId && selCurso"
                label="Quitar"
                icon="pi pi-times"
                class="p-button-sm p-button-outlined p-button-danger"
                :loading="asignandoId === data.id"
                @click="quitarAsignacion(data)"
              />
              <span v-else class="text-xs text-500">Ver en «Todas»</span>
            </template>
          </Column>
        </DataTable>
      </template>

      <p v-else-if="!loading && !loadingCatalogos && !loadingCapitulos" class="text-sm text-500">
        Elige <strong>colegio</strong> y <strong>materia</strong>, luego pulsa <strong>Cargar</strong>.
        Nivel, curso y capítulo son opcionales para filtrar.
      </p>
        </TabPanel>
      </TabView>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import ButtonPrime from 'primevue/button';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import TagPrime from 'primevue/tag';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import DjangoAuthGate from '@/components/estudyoo/DjangoAuthGate.vue';
import EstudyooLoadingBanner from '@/components/estudyoo/EstudyooLoadingBanner.vue';
import { ActividadesApi } from '@/services/EstudyooAdminApi';
import { isDjangoAdminAuthenticated, handleDjangoApiError } from '@/services/djangoAdminAuth';
import { compararCursos, labelNivel, NIVEL_OPTS, normalizarCodigoNivel } from '@/utils/nivelesEscolares';

const router = useRouter();
const toast = useToast();
const djangoOk = ref(isDjangoAdminAuthenticated());

const materias = ref([]);
const colegios = ref([]);
const cursos = ref([]);
const capitulos = ref([]);
const capitulosMateria = ref([]);
const tipos = ref([]);
const actividades = ref([]);
const asignacionesCurso = ref([]);

const selColegioId = ref(null);
const selMateria = ref(null);
const selNivel = ref(null);
const selCurso = ref(null);
const selCapitulo = ref(null);

const loading = ref(false);
const loadingCatalogos = ref(false);
const loadingColegios = ref(false);
const loadingCapitulos = ref(false);
const loadingCapitulosMateria = ref(false);
let catalogBootstrapDone = false;
let catalogBootstrapInFlight = false;
const panelListo = ref(false);
const soloPendientes = ref(false);
const asignandoTodas = ref(false);
const asignandoId = ref(null);
const corrigiendoNivel = ref(false);

const todasFilas = ref([]);
const todasTotal = ref(0);
const todasPage = ref(1);
const todasRows = ref(20);
const loadingTodas = ref(false);
const filtTodasQ = ref('');
const filtTodasColegioId = ref(null);
const filtTodasMateria = ref(null);
const filtTodasCurso = ref(null);
const filtTodasActiva = ref('');
const opcionesEstadoAsig = [
  { label: 'Todas', value: '' },
  { label: 'Solo activas', value: 'true' },
  { label: 'Solo inactivas', value: 'false' },
];

const mapTipos = computed(() => Object.fromEntries(tipos.value.map((t) => [t.id, t])));

const diag = ref({
  materias: null,
  cursos: null,
  colegios: null,
  actividades: null,
  capitulos: null,
});
const diagListo = ref(false);

const catalogoVacio = computed(() => {
  const d = diag.value;
  return (
    diagListo.value
    && (d.materias === 0 || d.materias == null)
    && (d.cursos === 0 || d.cursos == null)
  );
});

const colegioOpts = computed(() => {
  const byId = new Map();
  for (const c of colegios.value || []) {
    if (!c?.id) continue;
    byId.set(String(c.id), {
      id: c.id,
      label: c.label || c.nombre || `Colegio ${String(c.id).slice(0, 8)}`,
    });
  }
  // Completar desde cursos / materias si el listado de colegios vino vacío o incompleto.
  for (const cur of cursos.value || []) {
    const id = cur.colegio_id || cur.colegio;
    if (!id || byId.has(String(id))) continue;
    byId.set(String(id), {
      id,
      label: cur.colegio_nombre || `Colegio ${String(id).slice(0, 8)}`,
    });
  }
  for (const m of materias.value || []) {
    const id = m.colegio_id || m.colegio;
    if (!id || byId.has(String(id))) continue;
    byId.set(String(id), {
      id,
      label: `Colegio ${String(id).slice(0, 8)}`,
    });
  }
  return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label, 'es'));
});

const colegioNombrePorId = computed(() =>
  Object.fromEntries(colegioOpts.value.map((c) => [String(c.id), c.label])),
);

const materiasFiltradas = computed(() => {
  if (!selColegioId.value) return [];
  const cid = String(selColegioId.value);
  const matched = materias.value.filter((m) => {
    const mid = m.colegio_id || m.colegio;
    return !mid || String(mid) === cid;
  });
  return matched.length ? matched : materias.value;
});

function labelMateriaItem(m) {
  if (!m) return '';
  const n = String(m.nombre || m.glosa || '').trim();
  if (!n) return '';
  // Evitar mostrar UUID crudo o placeholders de hidratación.
  if (/^[0-9a-f-]{36}$/i.test(n)) return '';
  if (/^Materia\s+[0-9a-f-]{8}/i.test(n)) return '';
  return n;
}

/** Materias con nombre legible (glosa), sin duplicar por mismo nombre. */
const materiaOpts = computed(() => {
  const byLabel = new Map();
  for (const m of materiasFiltradas.value) {
    const label = labelMateriaItem(m);
    if (!label) continue;
    const key = label.toLowerCase();
    if (!byLabel.has(key)) byLabel.set(key, { ...m, label });
  }
  return [...byLabel.values()].sort((a, b) => a.label.localeCompare(b.label, 'es'));
});

const materiasSinAlcanceColegio = computed(() => {
  if (!selColegioId.value || !materias.value.length) return false;
  const cid = String(selColegioId.value);
  return !materias.value.some((m) => {
    const mid = m.colegio_id || m.colegio;
    return !mid || String(mid) === cid;
  });
});

const materiasFiltradasTodas = computed(() => {
  if (!filtTodasColegioId.value) return materias.value;
  const cid = String(filtTodasColegioId.value);
  const matched = materias.value.filter((m) => {
    const mid = m.colegio_id || m.colegio;
    return !mid || String(mid) === cid;
  });
  return matched.length ? matched : materias.value;
});

const materiaOptsTodas = computed(() => {
  const byLabel = new Map();
  for (const m of materiasFiltradasTodas.value) {
    const label = labelMateriaItem(m);
    if (!label) continue;
    const key = label.toLowerCase();
    if (!byLabel.has(key)) byLabel.set(key, { ...m, label });
  }
  return [...byLabel.values()].sort((a, b) => a.label.localeCompare(b.label, 'es'));
});

const cursosScope = computed(() => {
  if (!selColegioId.value) return [];
  const cid = String(selColegioId.value);
  const matched = cursos.value.filter((c) => colegioIdDe(c) === cid);
  if (matched.length) return matched;
  // Fallback: IDs de colegio inconsistentes entre APIs → no dejar el combo vacío.
  return [...cursos.value];
});

/** Siempre los 12 niveles estándar (no depende de cursos sincronizados). */
const nivelOpts = computed(() => NIVEL_OPTS);
function labelCursoSeccion(c) {
  if (!c) return '—';
  const niv = labelNivel(c.nivel_simce);
  const casa = (c.nombre_interno || '').trim();
  const letra = (c.letra || '').trim();
  if (casa && casa.toLowerCase() !== letra.toLowerCase()) {
    return `${niv} · ${casa}`;
  }
  if (letra && letra !== 'A') {
    return `${niv} ${letra}`;
  }
  return niv || '—';
}

const cursoOpts = computed(() => {
  let list = [...cursosScope.value];
  const nivel = normalizarCodigoNivel(selNivel.value);
  if (nivel) {
    list = list.filter((c) => normalizarCodigoNivel(c.nivel_simce) === nivel);
  }
  return list
    .sort(compararCursos)
    .map((c) => ({
      ...c,
      label: nivel ? labelCursoSeccion(c) : `${labelNivel(c.nivel_simce)} ${c.letra || ''}`.trim()
        + (c.nombre_interno ? ` · ${c.nombre_interno}` : ''),
    }));
});

const cursoPlaceholder = computed(() => {
  if (!selColegioId.value) return 'Primero elige colegio';
  if (!cursoOpts.value.length) return 'Sin cursos en este colegio';
  if (selNivel.value) return 'Sección A, B…';
  return 'Elegir curso (opcional para ver; obligatorio para asignar)';
});

const cursoOptsTodas = computed(() => {
  let base = [...cursos.value];
  if (filtTodasColegioId.value) {
    base = base.filter(
      (c) => String(c.colegio_id || c.colegio) === String(filtTodasColegioId.value),
    );
  }
  return base
    .sort(compararCursos)
    .map((c) => {
      const col = colegioNombrePorId.value[String(c.colegio_id || c.colegio)] || '';
      const curso = `${labelNivel(c.nivel_simce)} ${c.letra || ''}`.trim()
        + (c.nombre_interno ? ` · ${c.nombre_interno}` : '');
      return {
        ...c,
        label: col && !filtTodasColegioId.value ? `${col} · ${curso}` : curso,
      };
    });
});

const capituloOpts = computed(() => {
  const nivel = selNivel.value;
  return capitulos.value
    .filter((c) => !nivel || !c.nivel_simce || c.nivel_simce === nivel)
    .map((c) => {
      const nombre = String(c.nombre || c.glosa || '').trim() || 'Capítulo';
      const niv = c.nivel_simce ? ` · ${labelNivel(c.nivel_simce)}` : '';
      const inactive = c.activo === false ? ' · inactivo' : '';
      return {
        ...c,
        label: `${nombre}${niv}${inactive}`,
      };
    })
    .sort((a, b) => (a.orden || 0) - (b.orden || 0) || String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es'));
});

const capituloPlaceholder = computed(() => {
  if (!selColegioId.value) return 'Primero elige colegio';
  if (!selMateria.value) return 'Primero elige materia';
  if (loadingCapitulos.value) return 'Cargando…';
  if (!capituloOpts.value.length) return 'Sin capítulos (puedes cargar igual)';
  return 'Todos los capítulos (opcional)';
});

const capitulosSinNivel = computed(() => {
  if (!selMateria.value) return [];
  return capitulosMateria.value.filter((c) => !c.nivel_simce);
});

const filas = computed(() => {
  const asigPorActividad = new Map();
  for (const a of asignacionesCurso.value || []) {
    const aid = String(a.actividad_id || a.actividad || '').toLowerCase();
    if (!aid) continue;
    // Preferir asignación activa si hay duplicados.
    const prev = asigPorActividad.get(aid);
    if (!prev || (a.activa !== false && prev.activa === false)) {
      asigPorActividad.set(aid, a);
    }
  }
  return (actividades.value || [])
    .filter((a) => a.activa !== false)
    .map((a) => {
      const aid = String(a.id || '').toLowerCase();
      const asig = asigPorActividad.get(aid);
      return {
        ...a,
        asignada: !!asig && asig.activa !== false,
        asignacionId: asig?.id || null,
      };
    });
});

const asignadasCount = computed(() => filas.value.filter((f) => f.asignada).length);
const pendientesCount = computed(() => filas.value.filter((f) => !f.asignada).length);

const filasVista = computed(() => {
  const list = filas.value || [];
  const filtered = soloPendientes.value ? list.filter((f) => !f.asignada) : list;
  // Pendientes primero para que se vean al cargar.
  return [...filtered].sort((a, b) => Number(a.asignada) - Number(b.asignada) || String(a.titulo || '').localeCompare(String(b.titulo || ''), 'es'));
});

function labelMateria(m) {
  return labelMateriaItem(m) || '—';
}

function labelCapituloActividad(act) {
  if (!act) return '—';
  if (act.capitulo_nombre) return act.capitulo_nombre;
  const cid = String(act.capitulo_id || act.capitulo || '');
  if (!cid) return '—';
  const cap = [...capitulos.value, ...capitulosMateria.value].find((c) => String(c.id) === cid);
  return cap?.nombre || cid.slice(0, 8);
}

function labelCurso(c) {
  if (!c) return '—';
  return `${labelNivel(c.nivel_simce)} ${c.letra || ''}`.trim();
}

function labelCursoRow(row) {
  if (!row?.curso_nivel && !row?.curso_letra) return '—';
  const n = row.curso_nivel ? labelNivel(row.curso_nivel) : '';
  const letra = row.curso_letra || '';
  const interno = row.curso_nombre_interno ? ` · ${row.curso_nombre_interno}` : '';
  return `${n} ${letra}`.trim() + interno;
}

function labelTipo(tipoId) {
  const t = mapTipos.value[tipoId];
  return t ? (t.nombre || t.codigo) : '—';
}

function colegioIdDe(row) {
  if (!row) return '';
  const raw = row.colegio_id ?? row.colegio;
  if (raw && typeof raw === 'object') return String(raw.id || '');
  return raw != null && raw !== '' ? String(raw) : '';
}

function normalizarCursoItem(c) {
  if (!c?.id) return null;
  const colegio_id = colegioIdDe(c) || null;
  return {
    ...c,
    id: c.id,
    colegio_id,
    colegio: colegio_id,
    nivel_simce: normalizarCodigoNivel(c.nivel_simce),
    letra: c.letra || '',
    nombre_interno: c.nombre_interno || '',
  };
}

function normalizarListaCursos(list) {
  return (Array.isArray(list) ? list : [])
    .map(normalizarCursoItem)
    .filter(Boolean);
}

/** Elige el curso del nivel superior (preferencia sección A). */
function autoSeleccionarCursoDestino() {
  const list = cursoOpts.value || [];
  if (!list.length) {
    selCurso.value = null;
    return;
  }
  if (selCurso.value?.id) {
    const keep = list.find((c) => String(c.id) === String(selCurso.value.id));
    if (keep) {
      selCurso.value = keep;
      return;
    }
  }
  const preferA = list.find((c) => String(c.letra || '').toUpperCase() === 'A');
  selCurso.value = preferA || list[0];
}

async function asegurarCursosDelColegio() {
  if (!selColegioId.value) return;
  const cid = String(selColegioId.value);
  try {
    const extra = await ActividadesApi.listCursos({ colegio_id: cid }, { force: true });
    const norm = normalizarListaCursos(extra);
    for (const c of norm) {
      upsertById(cursos, c);
    }
    // Si el filtro por colegio_id no trajo nada, ya quedan los del bootstrap global.
  } catch (e) {
    /* silencioso: el combo usará lo ya cargado en bootstrap */
  }
}

function onDjangoAuth(ok) {
  djangoOk.value = !!ok;
  if (!ok) {
    catalogBootstrapDone = false;
    panelListo.value = false;
    diagListo.value = false;
    todasFilas.value = [];
    todasTotal.value = 0;
    return;
  }
  bootstrapCatalogo(true).then(() => cargarTodasAsignaciones());
}

async function cargarTiposCatalogo() {
  if (tipos.value.length) return;
  try {
    tipos.value = await ActividadesApi.listTipos({});
  } catch (e) {
    catchApi(e, 'Tipos de actividad');
  }
}

async function cargarCatalogosSiFalta() {
  if (catalogBootstrapDone || catalogBootstrapInFlight) return;
  await bootstrapCatalogo(true);
}

/** Carga colegios + materias + cursos al entrar (no espera a elegir colegio). */
async function bootstrapCatalogo(force = false) {
  if (catalogBootstrapInFlight) return;
  if (!force && catalogBootstrapDone) return;
  catalogBootstrapInFlight = true;
  loadingCatalogos.value = true;
  loadingColegios.value = true;
  diagListo.value = false;
  try {
    const [cols, mList, cList, acts, caps, asigsPage] = await Promise.all([
      ActividadesApi.listColegios({ page_size: 500 }).catch(() => []),
      ActividadesApi.listMaterias({}, { force }),
      ActividadesApi.listCursos({}, { force }),
      ActividadesApi.listActividades({}).catch(() => []),
      ActividadesApi.listCapitulos({ activo: 'all' }).catch(() => []),
      ActividadesApi.listAsignacionesPage({ page: 1, page_size: 100 }).catch(() => ({ results: [] })),
    ]);
    const listaCols = Array.isArray(cols) ? cols : cols.results || [];
    colegios.value = listaCols
      .filter((c) => c && c.id)
      .map((c) => ({
        id: c.id,
        nombre: c.nombre || c.Nombre,
        label: c.nombre || c.Nombre || `Colegio ${String(c.id).slice(0, 8)}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es'));
    materias.value = Array.isArray(mList) ? mList : [];
    cursos.value = normalizarListaCursos(cList);
    const actsList = Array.isArray(acts) ? acts : [];
    const capsList = Array.isArray(caps) ? caps : [];
    const asigsList = asigsPage?.results || [];

    // Hidratar catálogo desde actividades/asignaciones (cubre listados vacíos o incompletos).
    hidratarCatalogoDesdeDatos(actsList, asigsList, capsList);
    await resolverNombresMateriasYCapitulos();

    await cargarTiposCatalogo();
    diag.value = {
      colegios: colegioOpts.value.length,
      materias: materiaOpts.value.length || materias.value.filter((m) => labelMateriaItem(m)).length,
      cursos: cursos.value.length,
      actividades: actsList.length,
      capitulos: Math.max(capsList.length, new Set(actsList.map((a) => a.capitulo || a.capitulo_id).filter(Boolean)).size),
    };
    diagListo.value = true;
    catalogBootstrapDone = true;

    // Si hay un solo colegio usable, preseleccionarlo y su curso.
    if (!selColegioId.value && colegioOpts.value.length === 1) {
      selColegioId.value = colegioOpts.value[0].id;
      await asegurarCursosDelColegio();
      autoSeleccionarCursoDestino();
    }

    if (!materias.value.length && diag.value.actividades > 0) {
      toast.add({
        severity: 'error',
        summary: 'Inconsistencia',
        detail: `Hay ${diag.value.actividades} actividad(es) pero 0 materias. Revisa sync o el mantenedor de actividades.`,
        life: 10000,
      });
    } else if (!materias.value.length) {
      toast.add({
        severity: 'warn',
        summary: 'Sin materias',
        detail: 'No hay materias en Django. Sincroniza asignaturas o créalas antes de asignar.',
        life: 8000,
      });
    }
  } catch (e) {
    catchApi(e, 'Catálogo');
  } finally {
    loadingCatalogos.value = false;
    loadingColegios.value = false;
    catalogBootstrapInFlight = false;
  }
}

function upsertById(listRef, item) {
  if (!item?.id) return;
  const id = String(item.id);
  const arr = listRef.value || [];
  const idx = arr.findIndex((x) => String(x.id) === id);
  if (idx >= 0) {
    const prev = arr[idx];
    const merged = { ...prev, ...item };
    // No pisar un nombre bueno con placeholder/UUID.
    const prevOk = labelMateriaItem(prev) || (prev.nombre && !/^Capítulo\s+[0-9a-f-]/i.test(prev.nombre) ? prev.nombre : '');
    const nextOk = labelMateriaItem(item) || (item.nombre && !/^Capítulo\s+[0-9a-f-]/i.test(item.nombre) && !/^Materia\s+[0-9a-f-]/i.test(item.nombre) ? item.nombre : '');
    if (prevOk && !nextOk) merged.nombre = prev.nombre;
    listRef.value = [...arr.slice(0, idx), merged, ...arr.slice(idx + 1)];
  } else {
    listRef.value = [...arr, item];
  }
}

/** Completa colegios/materias/cursos/capítulos a partir de lo que ya existe en actividades. */
function hidratarCatalogoDesdeDatos(actsList, asigsList, capsList) {
  for (const a of asigsList || []) {
    if (a.colegio_id) {
      upsertById(colegios, {
        id: a.colegio_id,
        nombre: a.colegio_nombre || `Colegio ${String(a.colegio_id).slice(0, 8)}`,
        label: a.colegio_nombre || `Colegio ${String(a.colegio_id).slice(0, 8)}`,
      });
    }
    if (a.materia_id || a.materia) {
      upsertById(materias, {
        id: a.materia_id || a.materia,
        nombre: a.materia_nombre || '',
        colegio_id: a.colegio_id || null,
        colegio: a.colegio_id || null,
      });
    }
    if (a.curso_id || a.curso) {
      upsertById(cursos, {
        id: a.curso_id || a.curso,
        letra: a.curso_letra || '',
        nivel_simce: normalizarCodigoNivel(a.curso_nivel || ''),
        nombre_interno: a.curso_nombre_interno || '',
        colegio_id: a.colegio_id || null,
        colegio: a.colegio_id || null,
        colegio_nombre: a.colegio_nombre || '',
      });
    }
    if (a.capitulo_id) {
      upsertById(capitulosMateria, {
        id: a.capitulo_id,
        nombre: a.capitulo_nombre || `Capítulo ${String(a.capitulo_id).slice(0, 8)}`,
        codigo: a.capitulo_codigo || '',
        nivel_simce: a.curso_nivel || '',
        activo: true,
        _materia_id: a.materia_id || a.materia || null,
      });
    }
  }

  for (const act of actsList || []) {
    const mid = act.materia_id || act.materia;
    if (mid) {
      upsertById(materias, {
        id: mid,
        nombre: act.materia_nombre || '',
        colegio_id: act.colegio_id || null,
        colegio: act.colegio_id || null,
      });
    }
    const cid = act.capitulo_id || act.capitulo;
    if (cid) {
      upsertById(capitulosMateria, {
        id: cid,
        nombre: act.capitulo_nombre || '',
        codigo: act.capitulo_codigo || '',
        nivel_simce: act.nivel_simce || '',
        activo: act.capitulo_activo !== false,
        _materia_id: mid || null,
      });
    }
  }

  for (const cap of capsList || []) {
    upsertById(capitulosMateria, {
      ...cap,
      nombre: cap.nombre || cap.glosa || '',
    });
  }
}

/** Completa nombres reales vía API cuando la hidratación solo trae UUID. */
async function resolverNombresMateriasYCapitulos() {
  const pendientesMat = (materias.value || [])
    .filter((m) => m?.id && !labelMateriaItem(m))
    .slice(0, 40);
  if (pendientesMat.length) {
    const fetched = await Promise.all(
      pendientesMat.map((m) => ActividadesApi.getMateria(m.id).catch(() => null)),
    );
    for (const row of fetched) {
      if (!row?.id) continue;
      upsertById(materias, {
        id: row.id,
        nombre: row.nombre || row.glosa || '',
        codigo: row.codigo || '',
        colegio_id: row.colegio_id || row.colegio || null,
        colegio: row.colegio_id || row.colegio || null,
      });
    }
  }

  const pendientesCap = (capitulosMateria.value || [])
    .filter((c) => {
      const n = String(c.nombre || '').trim();
      return c?.id && (!n || /^Capítulo\s+[0-9a-f-]{8}/i.test(n));
    })
    .slice(0, 40);
  if (pendientesCap.length) {
    const fetched = await Promise.all(
      pendientesCap.map((c) => ActividadesApi.getCapitulo(c.id).catch(() => null)),
    );
    for (const row of fetched) {
      if (!row?.id) continue;
      upsertById(capitulosMateria, {
        id: row.id,
        nombre: row.nombre || row.glosa || '',
        codigo: row.codigo || '',
        nivel_simce: row.nivel_simce || '',
        activo: row.activo !== false,
        orden: row.orden,
      });
    }
  }
}

function onTabChange(e) {
  if (e.index === 0) {
    cargarCatalogosSiFalta();
    if (djangoOk.value && !todasFilas.value.length && !loadingTodas.value) {
      cargarTodasAsignaciones();
    }
  } else if (e.index === 1) {
    bootstrapCatalogo(false);
  }
}

function onFiltTodasColegioChange() {
  filtTodasMateria.value = null;
  filtTodasCurso.value = null;
  catalogBootstrapDone = false;
  if (filtTodasColegioId.value) {
    cargarCatalogos(true);
  }
  buscarTodasAsignaciones();
}

function paramsTodasAsignaciones() {
  const p = {
    page: todasPage.value,
    page_size: todasRows.value,
  };
  if (filtTodasQ.value?.trim()) p.q = filtTodasQ.value.trim();
  if (filtTodasColegioId.value) p.colegio_id = filtTodasColegioId.value;
  if (filtTodasMateria.value?.id) p.materia_id = filtTodasMateria.value.id;
  if (filtTodasCurso.value?.id) p.curso_id = filtTodasCurso.value.id;
  if (filtTodasActiva.value !== '') p.activa = filtTodasActiva.value;
  return p;
}

async function cargarTodasAsignaciones() {
  loadingTodas.value = true;
  try {
    const data = await ActividadesApi.listAsignacionesPage(paramsTodasAsignaciones());
    todasFilas.value = data.results || [];
    todasTotal.value = data.count ?? todasFilas.value.length;
  } catch (e) {
    catchApi(e, 'Asignaciones');
  } finally {
    loadingTodas.value = false;
  }
}

function buscarTodasAsignaciones() {
  todasPage.value = 1;
  cargarTodasAsignaciones();
}

function limpiarFiltrosTodas() {
  filtTodasQ.value = '';
  filtTodasColegioId.value = null;
  filtTodasMateria.value = null;
  filtTodasCurso.value = null;
  filtTodasActiva.value = '';
  todasPage.value = 1;
  cargarTodasAsignaciones();
}

function onTodasPage(event) {
  todasPage.value = event.page + 1;
  todasRows.value = event.rows;
  cargarTodasAsignaciones();
}

async function quitarAsignacionTodas(row) {
  if (!row?.id) return;
  asignandoId.value = row.id;
  try {
    await ActividadesApi.deleteAsignacion(row.id);
    toast.add({ severity: 'info', summary: 'Asignación eliminada', life: 3000 });
    await cargarTodasAsignaciones();
    if (panelListo.value && selCurso.value?.id) {
      asignacionesCurso.value = await ActividadesApi.listAsignaciones({ curso_id: selCurso.value.id });
    }
  } catch (e) {
    catchApi(e, 'Quitar asignación');
  } finally {
    asignandoId.value = null;
  }
}

async function cargarCapitulosMateriaSinNivel() {
  if (!selMateria.value?.id) return;
  loadingCapitulosMateria.value = true;
  try {
    // Incluir inactivos: aquí se asignan actividades ya creadas (pueden vivir en caps desactivados del sync).
    const params = { materia_id: selMateria.value.id, activo: 'all' };
    if (selCurso.value?.id) params.curso_id = selCurso.value.id;
    capitulosMateria.value = await ActividadesApi.listCapitulos(params);
  } catch (e) {
    catchApi(e, 'Capítulos (materia)');
  } finally {
    loadingCapitulosMateria.value = false;
  }
}

async function cargarCapitulosFiltro() {
  capitulos.value = [];
  if (!selMateria.value?.id) return;
  loadingCapitulos.value = true;
  try {
    const params = { materia_id: selMateria.value.id, activo: 'all' };
    if (selNivel.value) params.nivel_simce = selNivel.value;
    if (selCurso.value?.id) params.curso_id = selCurso.value.id;
    if (selColegioId.value) params.colegio_id = selColegioId.value;
    const fromApi = await ActividadesApi.listCapitulos(params);
    const mid = String(selMateria.value.id);
    const fromHydrate = (capitulosMateria.value || []).filter((c) => {
      const m = String(c._materia_id || '');
      if (m && m !== mid) return false;
      if (c.nivel_simce && selNivel.value && c.nivel_simce !== selNivel.value) return false;
      return true;
    });
    const byId = new Map();
    for (const c of [...(fromApi || []), ...fromHydrate]) {
      if (c?.id) byId.set(String(c.id), c);
    }
    capitulos.value = [...byId.values()];
    cargarCapitulosMateriaSinNivel();
  } catch (e) {
    const mid = String(selMateria.value.id);
    capitulos.value = (capitulosMateria.value || []).filter(
      (c) => String(c._materia_id || '') === mid
        && (!selNivel.value || !c.nivel_simce || c.nivel_simce === selNivel.value),
    );
    catchApi(e, 'Capítulos');
  } finally {
    loadingCapitulos.value = false;
  }
}

function onDjangoUnauthorized() {
  djangoOk.value = false;
  catalogBootstrapDone = false;
  panelListo.value = false;
}

function catchApi(e, ctx) {
  if (handleDjangoApiError(e, { toast, onUnauthorized: onDjangoUnauthorized })) return;
  const status = e.response?.status;
  if (status === 503 || status === 502 || status === 504) {
    toast.add({
      severity: 'error',
      summary: ctx || 'Servidor no disponible',
      detail: 'El backend Django no respondió (error 503). Puede estar reiniciándose en DigitalOcean — espera 1–2 minutos y recarga. Si persiste, revisa el deploy del servicio seal-app-rggvf.',
      life: 12000,
    });
    return;
  }
  toast.add({
    severity: 'error',
    summary: ctx || 'Error',
    detail: e.response?.data?.detail || e.message || 'Error desconocido',
    life: 6000,
  });
}

function onColegioChange() {
  selMateria.value = null;
  selNivel.value = null;
  selCurso.value = null;
  selCapitulo.value = null;
  capitulos.value = [];
  capitulosMateria.value = [];
  actividades.value = [];
  asignacionesCurso.value = [];
  panelListo.value = false;
  if (selColegioId.value && !materias.value.length) {
    bootstrapCatalogo(true);
  }
  if (selColegioId.value) {
    asegurarCursosDelColegio().then(() => autoSeleccionarCursoDestino());
  }
}

async function cargarCatalogos(force = false) {
  // Compat: filtros que pedían catálogo por colegio → bootstrap completo.
  await bootstrapCatalogo(force);
}

function onMateriaChange() {
  selNivel.value = null;
  selCurso.value = null;
  selCapitulo.value = null;
  capitulos.value = [];
  capitulosMateria.value = [];
  panelListo.value = false;
  if (selMateria.value?.id) {
    cargarCapitulosFiltro();
  }
}

function onNivelChange() {
  selCapitulo.value = null;
  panelListo.value = false;
  // Asegurar cursos del colegio y auto-seleccionar el del mismo nivel.
  asegurarCursosDelColegio().then(() => {
    autoSeleccionarCursoDestino();
    if (selMateria.value?.id) {
      cargarCapitulosFiltro();
    }
  });
}

function onCapituloChange() {
  panelListo.value = false;
  const nivCap = normalizarCodigoNivel(selCapitulo.value?.nivel_simce);
  if (nivCap) {
    selNivel.value = nivCap;
    autoSeleccionarCursoDestino();
  }
}

function onCursoChange() {
  const cursoId = selCurso.value?.id;
  const nivCurso = normalizarCodigoNivel(selCurso.value?.nivel_simce);
  if (nivCurso && nivCurso !== normalizarCodigoNivel(selNivel.value)) {
    selNivel.value = nivCurso;
  }
  // Tras filtrar por nivel, las options se regeneran: reenganchar por id.
  if (cursoId) {
    const match = cursoOpts.value.find((c) => String(c.id) === String(cursoId));
    if (match) selCurso.value = match;
  }
  if (panelListo.value && selMateria.value?.id) {
    refrescarAsignacionesCurso().catch((e) => catchApi(e, 'Asignaciones'));
    return;
  }
  panelListo.value = false;
  if (selMateria.value?.id) {
    cargarCapitulosFiltro();
  }
}

function limpiar() {
  selColegioId.value = null;
  selMateria.value = null;
  selNivel.value = null;
  selCurso.value = null;
  selCapitulo.value = null;
  capitulos.value = [];
  capitulosMateria.value = [];
  actividades.value = [];
  asignacionesCurso.value = [];
  panelListo.value = false;
}

async function corregirNivelCapitulo() {
  if (!selCapitulo.value?.id || !selNivel.value) return;
  corrigiendoNivel.value = true;
  try {
    await ActividadesApi.patchCapitulo(selCapitulo.value.id, { nivel_simce: selNivel.value });
    selCapitulo.value = { ...selCapitulo.value, nivel_simce: selNivel.value };
    const idx = capitulos.value.findIndex((c) => c.id === selCapitulo.value.id);
    if (idx >= 0) capitulos.value[idx] = { ...capitulos.value[idx], nivel_simce: selNivel.value };
    toast.add({
      severity: 'success',
      summary: 'Nivel actualizado',
      detail: `Capítulo vinculado a ${labelNivel(selNivel.value)}.`,
      life: 5000,
    });
    await cargarCapitulosFiltro();
    if (selCapitulo.value?.id) {
      selCapitulo.value = capituloOpts.value.find((c) => c.id === selCapitulo.value.id) || selCapitulo.value;
    }
  } catch (e) {
    catchApi(e, 'Capítulo');
  } finally {
    corrigiendoNivel.value = false;
  }
}

async function cargarPanel() {
  if (!selColegioId.value) {
    toast.add({ severity: 'warn', summary: 'Filtros', detail: 'Elige un colegio.', life: 4000 });
    return;
  }
  if (!selMateria.value?.id) {
    toast.add({ severity: 'warn', summary: 'Filtros', detail: 'Elige una materia.', life: 4000 });
    return;
  }
  if (
    selCapitulo.value?.nivel_simce
    && selNivel.value
    && selCapitulo.value.nivel_simce !== selNivel.value
  ) {
    toast.add({
      severity: 'warn',
      summary: 'Nivel distinto',
      detail: `El capítulo es de ${labelNivel(selCapitulo.value.nivel_simce)} y el filtro de ${labelNivel(selNivel.value)}.`,
      life: 5000,
    });
    return;
  }

  loading.value = true;
  panelListo.value = false;
  try {
    const actParams = { materia_id: selMateria.value.id };
    if (selCapitulo.value?.id) actParams.capitulo_id = selCapitulo.value.id;

    const asigParams = { materia_id: selMateria.value.id };
    if (selCurso.value?.id) {
      asigParams.curso_id = selCurso.value.id;
    } else {
      asigParams.colegio_id = selColegioId.value;
    }
    if (selCapitulo.value?.id) asigParams.capitulo_id = selCapitulo.value.id;

    const [acts, asigs] = await Promise.all([
      ActividadesApi.listActividades(actParams),
      ActividadesApi.listAsignaciones(asigParams),
    ]);

    let lista = acts || [];
    if (selNivel.value && !selCapitulo.value?.id) {
      lista = lista.filter((a) => {
        const niv = a.nivel_simce || '';
        if (!niv) return true;
        return niv === selNivel.value;
      });
    }

    actividades.value = lista;
    asignacionesCurso.value = asigs || [];
    soloPendientes.value = false;
    panelListo.value = true;

    if (!lista.length) {
      toast.add({
        severity: 'info',
        summary: 'Sin actividades',
        detail: 'No hay actividades activas con estos filtros. Prueba sin nivel/capítulo o revisa el sync.',
        life: 6000,
      });
    }
  } catch (e) {
    catchApi(e, 'Cargar actividades');
  } finally {
    loading.value = false;
  }
}

async function asignarUna(act) {
  if (!selCurso.value?.id) {
    toast.add({
      severity: 'warn',
      summary: 'Curso destino',
      detail: 'Elige el curso al que quieres asignar esta actividad.',
      life: 4500,
    });
    return;
  }
  if (!selMateria.value?.id) return;
  asignandoId.value = act.id;
  try {
    const created = await ActividadesApi.createAsignacion({
      actividad_id: act.id,
      curso_id: selCurso.value.id,
      materia_id: act.materia_id || selMateria.value.id,
      casa_id: null,
      activa: true,
    });
    await refrescarAsignacionesCurso(created);
    await cargarTodasAsignaciones();
    toast.add({ severity: 'success', summary: 'Asignada', detail: act.titulo, life: 3000 });
  } catch (e) {
    catchApi(e, 'Asignar');
  } finally {
    asignandoId.value = null;
  }
}

async function refrescarAsignacionesCurso(createdRow = null) {
  const params = { materia_id: selMateria.value?.id };
  if (selCurso.value?.id) {
    params.curso_id = selCurso.value.id;
  } else if (selColegioId.value) {
    params.colegio_id = selColegioId.value;
  } else {
    return;
  }
  if (selCapitulo.value?.id) params.capitulo_id = selCapitulo.value.id;
  const list = await ActividadesApi.listAsignaciones(params);
  asignacionesCurso.value = list;
  if (createdRow?.id) {
    const aid = createdRow.actividad_id || createdRow.actividad;
    const idx = asignacionesCurso.value.findIndex(
      (a) => String(a.actividad_id || a.actividad) === String(aid),
    );
    if (idx < 0) asignacionesCurso.value = [createdRow, ...asignacionesCurso.value];
  }
}

async function quitarAsignacion(row) {
  if (!row.asignacionId) return;
  asignandoId.value = row.id;
  try {
    await ActividadesApi.deleteAsignacion(row.asignacionId);
    await refrescarAsignacionesCurso();
    await cargarTodasAsignaciones();
    toast.add({ severity: 'info', summary: 'Asignación eliminada', life: 3000 });
  } catch (e) {
    catchApi(e, 'Quitar asignación');
  } finally {
    asignandoId.value = null;
  }
}

async function asignarTodasPendientes() {
  if (!selCurso.value?.id) {
    toast.add({
      severity: 'warn',
      summary: 'Curso destino',
      detail: 'Elige el curso al que quieres asignar las pendientes.',
      life: 4500,
    });
    return;
  }
  const pendientes = filas.value.filter((f) => !f.asignada);
  if (!pendientes.length) return;
  asignandoTodas.value = true;
  let creadas = 0;
  let errores = 0;
  try {
    for (const act of pendientes) {
      try {
        await ActividadesApi.createAsignacion({
          actividad_id: act.id,
          curso_id: selCurso.value.id,
          materia_id: act.materia_id || selMateria.value.id,
          casa_id: null,
          activa: true,
        });
        creadas += 1;
      } catch {
        errores += 1;
      }
    }
    await refrescarAsignacionesCurso();
    await cargarTodasAsignaciones();
    toast.add({
      severity: errores ? 'warn' : 'success',
      summary: 'Asignación masiva',
      detail: `${creadas} creada(s)${errores ? `, ${errores} error(es)` : ''}.`,
      life: 6000,
    });
  } finally {
    asignandoTodas.value = false;
  }
}

onMounted(() => {
  if (djangoOk.value) bootstrapCatalogo(true).then(() => cargarTodasAsignaciones());
});
</script>

<style scoped>
.asig-hub {
  max-width: 1100px;
}

.asig-stat {
  min-width: 7rem;
}

.asig-loading-banner {
  background: var(--surface-ground);
}

.asig-filters--dim {
  opacity: 0.88;
  transition: opacity 0.2s ease;
}
</style>
